import { domMax, m } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CapiPlp, GridSize } from 'types/capi/plp';
import type {
  ProductListItem,
  ProductListItemExternal,
  ProductListItemInternal
} from 'types/capi/shared';

import FramerMotion from '~/components/FramerMotion';
import ProductCard from '~/components/ProductCard';
import usePageData from '~/hooks/usePageData';
import useQsParams from '~/hooks/useQsParams';

import { MidModuleSection } from '../ModuleSection';
import Pagination from './Pagination';

const gridStyles = {
  large: 'grid-cols-1 md:grid-cols-3 ',
  small: 'grid-cols-2 md:grid-cols-4'
};

const Products = ({
  gridSize = 'small',
  allowNotifications,
  listId
}: {
  allowNotifications: boolean;
  gridSize: GridSize;
  listId: string;
}) => {
  const { q } = useQsParams();
  const plp = usePageData<CapiPlp>();
  const pageType = q ? 'search' : 'plp';

  return (
    <div className={!q ? 'min-h-screen py-6 desktop:w-desktop' : 'py-6'}>
      {plp.products.length > 0 ? (
        <div className="flex flex-col gap-4">
          <div
            className={`grid gap-x-2 gap-y-4 md:gap-6 ${gridStyles[gridSize]}`}
          >
            {plp.products.map((product, index) => (
              <React.Fragment key={`plp-product-${index}`}>
                {index === 12 && <MidModuleSection />}
                <FramerMotion key={product.id} feature={domMax}>
                  <m.div layout>
                    {product.type === 'external' ? (
                      <ProductCardExternal
                        key={`product-${product.id}-${index}`}
                        allowNotifications={allowNotifications}
                        listId={listId}
                        product={product as ProductListItemExternal}
                        position={index + 1}
                        pageType={pageType}
                      />
                    ) : (
                      <ProductCardInternal
                        product={product as ProductListItemInternal}
                        allowNotifications={allowNotifications}
                        listId={listId}
                        position={index + 1}
                        gridSize={gridSize}
                        pageType={pageType}
                      />
                    )}
                  </m.div>
                </FramerMotion>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-6">
            <Pagination pagination={plp.pagination} />
          </div>
        </div>
      ) : (
        <NoResultsMessage />
      )}
    </div>
  );
};

function ProductCardInternal({
  product,
  allowNotifications,
  gridSize,
  listId,
  position,
  pageType
}: {
  allowNotifications: boolean;
  gridSize: GridSize;
  listId: string;
  pageType: 'plp' | 'search';
  position: number;
  product: ProductListItemInternal;
}) {
  function onProductCardClick(product: ProductListItem) {
    if (product.queryID) localStorage.setItem('queryID', product.queryID);
  }

  return (
    <ProductCard.Internal
      allowNotifications={allowNotifications}
      cardSize={gridSize}
      listId={listId}
      position={position}
      padImage={true}
      product={product}
      imageLoading={position <= 10 ? 'eager' : 'lazy'}
      onClick={() => onProductCardClick(product)}
      pageType={pageType}
      pagePosition="PLP Main List"
    />
  );
}

function ProductCardExternal({
  product,
  allowNotifications,
  listId,
  position,
  pageType
}: {
  allowNotifications: boolean;
  listId: string;
  pageType: 'plp' | 'search';
  position: number;
  product: ProductListItemExternal;
}) {
  return (
    <ProductCard.External
      product={product}
      allowNotifications={allowNotifications}
      listId={listId}
      position={position}
      padImage
      className="h-auto min-w-40"
      imageLoading="eager"
      cardSize="small"
      pagePosition="PLP Quick Buy"
      overrideInteractionPagePosition={true}
      pageType={pageType}
    />
  );
}

const NoResultsMessage = () => {
  const { t } = useTranslation('plp');

  return (
    <h2 className="text-caption-grey flex items-center justify-center py-6 text-center">
      {t('noResults')}
    </h2>
  );
};

export default Products;
